import {useState, useCallback, useEffect, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {Tab} from 'presentation/components/HeaderTabNavigation/types';
import {BillData} from 'domain/usecases';
import {MyPaymentsEnum} from 'presentation/constants';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatPriceBill} from 'presentation/utils';
import {useNewBillsBemobiFeatureFlag} from 'presentation/pages/private/hooks/useNewBillsBemobiFeatureFlag';

import type {OpenBillRow} from '../components/OpenBillsTable/types';
import {OpenBillStatusEnum} from '../components/OpenBillsTable/types';

const {newOpenInvoicesPage, newHistoryPage} = translations['pt-br'].privateFlow.myFinancial;

export const useOpenBillsPage = (rows: OpenBillRow[]) => {
	const isRowSelectable = (row: OpenBillRow) =>
		!row.isSaoLucas && row.cdEspecieTitulo !== 70;
	const sumSelectedBills = (billRows: OpenBillRow[], selectedIds: Set<string>): string => {
		const total = billRows
			.filter(row => selectedIds.has(row.cdObrigacao))
			.reduce((acc, row) => acc + row.vlObrigacao, 0);
		return formatPriceBill(total);
	};

	const hasSelectedOverdueBills = (billRows: OpenBillRow[], selectedIds: Set<string>): boolean =>
		billRows.some(
			row => selectedIds.has(row.cdObrigacao) && row.status === OpenBillStatusEnum.OVERDUE,
		);

	const {isEnabled: newBillsFlagIsEnabled} = useNewBillsBemobiFeatureFlag();
	const {isMobile} = useMediaQueries();
	const location = useLocation();
	const navigate = useNavigate();
	const initialRouteState = useRef(location.state);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [selectedRow, setSelectedRow] = useState<OpenBillRow | null>(null);
	const [hasPendingInitialDrawer, setHasPendingInitialDrawer] = useState(
		() => !!location.state?.preselectedBill,
	);

	const handleSelectionChange = useCallback((ids: Set<string>) => setSelectedIds(ids), []);

	const handleToggleRow = useCallback(
		(id: string) => {
			const row = rows.find(r => r.cdObrigacao === id);
			if (row && !isRowSelectable(row)) return;
			setSelectedIds(prev => {
				const next = new Set(prev);
				if (next.has(id)) {
					next.delete(id);
				} else {
					next.add(id);
				}
				return next;
			});
		},
		[rows],
	);

	const handleViewDetails = (row: OpenBillRow) => {
		setSelectedRow(row);
		setDrawerOpen(true);
	};

	useEffect(() => {
		if (location.state) {
			navigate(location.pathname, {replace: true, state: null});
		}
	}, []);

	useEffect(() => {
		if (!hasPendingInitialDrawer || rows.length === 0) return;
		const preselectedBill = initialRouteState.current?.preselectedBill as
			| BillData
			| undefined;
		if (!preselectedBill) {
			setHasPendingInitialDrawer(false);
			return;
		}
		const matchingRow = rows.find(r => r.cdObrigacao === String(preselectedBill.cdObrigacao));
		if (matchingRow) {
			handleViewDetails(matchingRow);
		}
		setHasPendingInitialDrawer(false);
		initialRouteState.current = null;
	}, [rows, hasPendingInitialDrawer]);

	const handleCloseDrawer = () => {
		setDrawerOpen(false);
		setSelectedRow(null);
	};
	const showAsterisk = hasSelectedOverdueBills(rows, selectedIds);
	const totalSelectedFormatted = sumSelectedBills(rows, selectedIds);

	const tabs: Tab[] = [
		{route: MyPaymentsEnum.OPEN_BILLS, name: newOpenInvoicesPage},
		{route: MyPaymentsEnum.PAYMENTS_HISTORY, name: newHistoryPage, id: 'payments-history-tab'},
	];

	return {
		newBillsFlagIsEnabled,
		isMobile,
		handleSelectionChange,
		handleToggleRow,
		handleViewDetails,
		handleCloseDrawer,
		showAsterisk,
		totalSelectedFormatted,
		tabs,
		drawerOpen,
		selectedRow,
		selectedIds,
		hasPendingInitialDrawer,
	};
};
